import { AxiosError } from 'axios';

/**
 * Error categories for API errors
 */
export enum ErrorCategory {
  /** Network connectivity issues */
  NETWORK = 'NETWORK',
  /** Authentication/authorization failures */
  AUTH = 'AUTH',
  /** Validation errors (400, 422) */
  VALIDATION = 'VALIDATION',
  /** Server errors (500, 502, 503) */
  SERVER = 'SERVER',
  /** Unknown or unhandled errors */
  UNKNOWN = 'UNKNOWN',
}

/**
 * Custom API error class with categorization
 *
 * Extends Error to provide structured error information
 * for better error handling and user messaging.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public category: ErrorCategory,
    public statusCode?: number,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Categorizes an error based on its type and status code
 *
 * @param error - The error to categorize (unknown type)
 * @returns An ApiError with appropriate category
 */
export function categorizeError(error: unknown): ApiError {
  // Handle AxiosError
  if (error instanceof AxiosError) {
    const statusCode = error.response?.status;

    // Network errors (no response)
    if (!error.response) {
      return new ApiError(
        'Network error. Please check your connection.',
        ErrorCategory.NETWORK,
        undefined,
        error
      );
    }

    // Authentication errors (401, 403)
    if (statusCode === 401 || statusCode === 403) {
      return new ApiError(
        statusCode === 401
          ? 'Please log in to continue.'
          : 'You do not have permission to access this resource.',
        ErrorCategory.AUTH,
        statusCode,
        error
      );
    }

    // Validation errors (400, 422)
    if (statusCode === 400 || statusCode === 422) {
      const message = error.response?.data?.message || 'Invalid request. Please check your input.';
      return new ApiError(message, ErrorCategory.VALIDATION, statusCode, error);
    }

    // Server errors (500, 502, 503, 504)
    if (statusCode && statusCode >= 500) {
      return new ApiError(
        'Server error. Please try again later.',
        ErrorCategory.SERVER,
        statusCode,
        error
      );
    }

    // Other HTTP errors
    return new ApiError(
      error.response?.data?.message || error.message || 'An error occurred.',
      ErrorCategory.UNKNOWN,
      statusCode,
      error
    );
  }

  // Handle ApiError instances (already categorized)
  if (error instanceof ApiError) {
    return error;
  }

  // Handle generic Error instances
  if (error instanceof Error) {
    return new ApiError(error.message, ErrorCategory.UNKNOWN, undefined, error);
  }

  // Handle unknown error types
  return new ApiError('An unexpected error occurred.', ErrorCategory.UNKNOWN, undefined, error);
}
